import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { format } from "date-fns";
import ShiftTag from "./ShiftTag";
import api from "../../Utils/api";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { clearCopy } from "../../Store/Actions/copyActions";

import { DateTime } from "luxon";

export default function CalendarCell({
  date,
  openDialog,
  entries,
  empty,
  openModifyDialog,

  showInsight, // ! => Showing Insight
  displayDay, // ! => Showing Date

  schedules, // ! Insight => Calc Total Time on Day

  period,
  simple,
  locationID,
  getSchedules,
  timeoff,
  location, // ! Only in Locations
  userID, // ! Only in Locations

  dayEvents, //! Display for day events only in Date view
  getDayEvents,
}) {
  const [elementOver, setElementOver] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  const dispatch = useDispatch();

  const copy = useSelector((state) => state.copy).copy;
  const copiedShift = useSelector((state) => state.copy).copiedShift;

  const setDay = (origin, target) => {
    let newDate = new Date(target);
    let newYear = newDate.getFullYear();
    let newMonth = newDate.getMonth();
    let newDay = newDate.getDate();

    // Update startTime with the same time, new date
    let originalStartTime = new Date(origin);
    originalStartTime.setFullYear(newYear, newMonth, newDay);
    return originalStartTime;
  };

  const onDrop = async (e) => {
    if (timeoff) {
      setElementOver(false);
      return;
    }

    setElementOver(false);
    const data = e.dataTransfer.getData("application/json");
    let receiveData = JSON.parse(data);
    if (receiveData.type == "update") {
      let updatedData = receiveData.updatedData;

      // ! Means => Get locationID ⬅ CalendarCell
      if (!location) updatedData["locationID"] = locationID;

      if (location) updatedData["userID"] = userID;
      updatedData["startTime"] = setDay(
        updatedData["startTime"],
        date
      ).toISOString();
      updatedData["endTime"] = setDay(
        updatedData["endTime"],
        date
      ).toISOString();

      if (
        setDay(updatedData["startTime"], date) >
        setDay(updatedData["endTime"], date)
      ) {
        const nextDay = new Date(
          setDay(updatedData["endTime"], date).getTime() + 86400000
        ).toISOString();
        updatedData["next"] = true;
        updatedData["endTime"] = nextDay;
      }

      if (updatedData["breakStartTime"])
        updatedData["breakStartTime"] = setDay(
          updatedData["breakStartTime"],
          date
        ).toISOString();

      if (updatedData["breakEndTime"])
        updatedData["breakEndTime"] = setDay(
          updatedData["breakEndTime"],
          date
        ).toISOString();

      const reqbody = {
        _id: updatedData.scheduleID,
        updatedData,
      };

      const res = await api.post("/schedule/update", reqbody);

      if (res.data.success) {
        toast.success("Success");
        getSchedules();
      } else {
        toast.warn(res.data.message);
      }
    } else if (receiveData.type == "create") {
      let createData = receiveData.data;

      createData["locationID"] = locationID;
      createData["startTime"] = setDay(createData["startTime"], date);
      createData["endTime"] = setDay(createData["endTime"], date);

      if (
        setDay(createData["startTime"], date) >
        setDay(createData["endTime"], date)
      ) {
        const nextDay = new Date(
          setDay(createData["endTime"], date).getTime() + 86400000
        ).toISOString();
        createData["next"] = true;
        createData["endTime"] = nextDay;
      }

      const res = await api.post("/schedule", createData);

      if (res.data.success) {
        toast.success("Success");
        getSchedules();
      } else if (res.data.status == "warn") {
        toast.error(res.data.message);
      } else {
        toast.error(res.data.message || "Failed to create schedule");
      }
    }
  };

  const onPaste = async (e, date) => {
    e.stopPropagation();

    // ! NO { ... } ➡ Redux Mutation Error
    let createData = { ...copiedShift };

    if (!location) {
      createData["locationID"] = locationID; // ! Paste ⬇ LOCATION
    } else {
      createData["userID"] = userID; // ! Paste ⬇ USER
    }

    createData["startTime"] = setDay(
      createData["startTime"],
      date
    ).toISOString();
    createData["endTime"] = setDay(createData["endTime"], date).toISOString();
    if (createData["breakStartTime"])
      createData["breakStartTime"] = setDay(
        createData["breakStartTime"],
        date
      ).toISOString();
    if (createData["breakEndTime"])
      createData["breakEndTime"] = setDay(
        createData["breakEndTime"],
        date
      ).toISOString();

    if (
      setDay(createData["startTime"], date) >
      setDay(createData["endTime"], date)
    ) {
      const nextDay = new Date(
        setDay(createData["endTime"], date).getTime() + 86400000
      ).toISOString();
      createData["next"] = true;
      createData["endTime"] = nextDay;
    }

    // alert(JSON.stringify(createData, null, 2));

    const res = await api.post("/schedule", createData);

    if (res.data.success) {
      toast.success("Event added successfully");
      getSchedules();
    } else if (res.data.status == "warn") {
      toast.warn(res.data.message);
    } else {
      toast.error("Failed to add event");
      return;
    }
  };

  const handleClickCreateNewShift = () => {
    dispatch(clearCopy());
    openDialog(date, locationID, userID);
  };

  const dayStr = format(date, "yyyy-MM-dd");
  const dayEvent = dayEvents
    ? dayEvents?.filter((de) => de.date?.split("T")[0] == dayStr)[0]
    : "";

  if (displayDay) {
    return (
      <div
        onClick={() => setShowPopover(true)}
        style={{ width: `${100 / Number(period)}%` }}
        className={`relative min-h-[30px] flex-shrink-0 bg-grey-50 border-l border-b border-gray-400 p-1 
          transition-shadow duration-100 text-left flex items-end justiy-center 
          hover:bg-gray-50  cursor-pointer`}
      >
        <div>{format(date, "EEE, dd")}</div>
        <div>
          {dayEvent && dayEvent?.public_holiday && (
            <span className="ml-2 text-[10px]">
              <i class="fa-solid fa-champagne-glasses"></i>
            </span>
          )}
          {dayEvent && dayEvent?.block_timeoff && (
            <span className="ml-2 text-[10px]">
              <i className="fa fa-ban" />
            </span>
          )}
        </div>
        {showPopover && (
          <DayPopover
            date={date}
            dayEvent={dayEvent}
            onClose={() => {
              setShowPopover(false);
              getDayEvents();
            }}
          />
        )}
      </div>
    );
  } else if (showInsight) {
    return (
      <div
        style={{ width: `${100 / Number(period)}%` }}
        className={`relative min-h-[30px] flex-shrink-0 bg-grey-50 p-1 
          transition-shadow duration-100 text-center flex items-end justify-center 
          hover:bg-gray-50  cursor-pointer`}
      >
        <div className="bg-gray-100 rounded-md flex flex-wrap justify-center w-full">
          {/* {date.toISOString()} */}
          {/* <div className="w-full text-center text-sm font-semibold text-gray-500">
            € 0
          </div> */}
          <div className="w-full py-2 text-center text-xs text-gray-400">
            <span className="mr-2 text-gray-700">0.00 %</span>
            <span className="text-gray-700">{getShiftTimeByDate(schedules, date)}h</span>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        onDragLeave={(e) => setElementOver(false)}
        onDragEnter={(e) => setElementOver(true)}
        onDragOver={(e) => {
          e.preventDefault(); // This is critical
          // console.log("onDragOver");
        }}
        onDrop={onDrop}
        style={{ width: `${100 / Number(period)}%` }}
        className={`relative min-h-[100px]  flex-shrink-0 ${
          elementOver ? "bg-gray-100" : "bg-grey-50"
        } border-l border-b border-gray-200  p-1 pb-6   transition-shadow duration-100`}
      >
        {!simple && !timeoff && (
          <div>
            <div
              onClick={handleClickCreateNewShift}
              className={`absolute right-[calc(50%-0.875rem)] bottom-0 z-3 w-7 h-7 flex items-center justify-center text-gray-400 text-lg font-medium rounded-full hover:bg-gray-100 transition-colors duration-150 cursor-pointer`}
            >
              +
            </div>
            {copy && (
              <i
                onClick={(e) => onPaste(e, date)}
                className="fa-solid fa-paste absolute right-[calc(50%-2rem)] -bottom-0.5 z-3 w-7 h-7 flex items-center justify-center text-gray-400 text-xs font-medium rounded-full hover:bg-gray-100 transition-colors duration-150 cursor-pointer"
              ></i>
            )}
          </div>
        )}

        {/* Top-right date label */}

        {!empty &&
          entries.map((entry) => (
            <div key={entry._id} className="flex flex-col gap-2">
              <ShiftTag
                openModifyDialog={() => openModifyDialog(entry)}
                openDialog={openDialog}
                date={date}
                // locationID={locationID}
                location={location} // ! For Display Location on Member view
                {...entry}
              />
            </div>
          ))}
      </div>
    );
  }
}

// ToggleSwitch component for the popover
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-16 h-7 flex items-center rounded-full p-1 transition-colors duration-200 border-2
        ${
          checked ? "bg-gray-100 border-gray-600" : "bg-white border-gray-600"
        }`}
    >
      <span
        className={`w-5 h-5 rounded-full shadow-md transform transition-transform duration-200
          ${checked ? "bg-gray-600 translate-x-8" : "bg-gray-600"}
        `}
      />
      <span
        className={`ml-2 text-xs font-bold ${
          checked ? "text-gray-700 -translate-x-5" : "text-gray-700"
        }`}
      >
        {checked ? "ON" : "OFF"}
      </span>
    </button>
  );
}

function DayPopover({ date, onClose, dayEvent }) {
  const dayName = format(date, "EEEE");
  const day = format(date, "do");
  const monthYear = format(date, "MMMM yyyy");
  const dayStr = format(date, "yyyy-MM-dd");

  const dialogRef = useRef(null);

  const [form, setForm] = useState({
    blockTimeOff: dayEvent?.block_timeoff,
    publicHoliday: dayEvent?.public_holiday,
  });

  // Outside click to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/schedule/set_day_event", {
        date: dayStr.split("T")[0],
        block_timeoff: form.blockTimeOff,
        public_holiday: form.publicHoliday,
      });

      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/20 flex items-start pt-20 justify-center">
      <form
        onSubmit={handleSave}
        ref={dialogRef}
        className="bg-white w-full max-w-md rounded-2xl shadow-xl border p-6 text-left animate-fade-in"
      >
        <div className="text-2xl font-bold mb-1">{dayName}</div>
        <div className="text-gray-500 mb-4 text-lg">
          {day} {monthYear}
        </div>

        <div className="flex items-center justify-between mb-3">
          <label className="font-medium">Block time off requests</label>
          <input
            type="checkbox"
            name="blockTimeOff"
            checked={form.blockTimeOff}
            onChange={handleChange}
            className="w-5 h-5"
          />
        </div>

        <div className="flex items-center justify-between mb-6">
          <label className="font-medium">Public holiday</label>
          <input
            type="checkbox"
            name="publicHoliday"
            checked={form.publicHoliday}
            onChange={handleChange}
            className="w-5 h-5"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function getShiftTimeByDate(schedules, targetDate) {
  let totalShiftHours = 0;

  // Convert targetDate to Date object (midnight)
  const dayStart = new Date(targetDate);
  dayStart.setFullYear(dayStart.getUTCFullYear());
  dayStart.setMonth(dayStart.getUTCMonth());
  dayStart.setDate(dayStart.getUTCDate());
  dayStart.setHours(0);
  dayStart.setMinutes(0);
  dayStart.setSeconds(0);

  // console.log("Day Target: ", targetDate);
  // console.log("Day Start: ", dayStart);

  // End of day (23:59:59.999)
  const dayEnd = new Date(targetDate);
  dayEnd.setFullYear(dayEnd.getUTCFullYear());
  dayEnd.setMonth(dayEnd.getUTCMonth());
  dayEnd.setDate(dayEnd.getUTCDate());
  dayEnd.setHours(23);
  dayEnd.setMinutes(59);
  dayEnd.setSeconds(59);

  schedules.forEach((schedule) => {
    // Skip non-shift schedules
    if (["leave", "unavailable"].includes(schedule.type)) return;

    const schedStart = new Date(schedule.startTime);
    schedStart.setFullYear(schedStart.getUTCFullYear());
    schedStart.setMonth(schedStart.getUTCMonth());
    schedStart.setDate(schedStart.getUTCDate());
    schedStart.setHours(schedStart.getUTCHours());
    schedStart.setMinutes(schedStart.getUTCMinutes());
    schedStart.setSeconds(schedStart.getUTCSeconds());

    const schedEnd = new Date(schedule.endTime);
    schedEnd.setFullYear(schedEnd.getUTCFullYear());
    schedEnd.setMonth(schedEnd.getUTCMonth());
    schedEnd.setDate(schedEnd.getUTCDate());
    schedEnd.setHours(schedEnd.getUTCHours());
    schedEnd.setMinutes(schedEnd.getUTCMinutes());
    schedEnd.setSeconds(schedEnd.getUTCSeconds());

    // console.log("Schedule Start: ", schedStart);
    // console.log("Schedule End: ", schedEnd);

    // Check if schedule overlaps with target date
    const overlapStart = schedStart > dayStart ? schedStart : dayStart;
    const overlapEnd = schedEnd < dayEnd ? schedEnd : dayEnd;

    if (overlapStart < overlapEnd) {
      let hours = (overlapEnd - overlapStart) / (1000 * 60 * 60);

      // Subtract break time if exists and overlaps this day
      if (schedule.breakStartTime && schedule.breakEndTime) {
        const breakStart = new Date(schedule.breakStartTime);
        breakStart.setFullYear(breakStart.getUTCFullYear());
        breakStart.setMonth(breakStart.getUTCMonth());
        breakStart.setDate(breakStart.getUTCDate());
        breakStart.setHours(breakStart.getUTCHours());
        breakStart.setMinutes(breakStart.getUTCMinutes());
        breakStart.setSeconds(breakStart.getUTCSeconds());

        const breakEnd = new Date(schedule.breakEndTime);
        breakEnd.setFullYear(breakEnd.getUTCFullYear());
        breakEnd.setMonth(breakEnd.getUTCMonth());
        breakEnd.setDate(breakEnd.getUTCDate());
        breakEnd.setHours(breakEnd.getUTCHours());
        breakEnd.setMinutes(breakEnd.getUTCMinutes());
        breakEnd.setSeconds(breakEnd.getUTCSeconds());

        const breakOverlapStart = breakStart > dayStart ? breakStart : dayStart;
        const breakOverlapEnd = breakEnd < dayEnd ? breakEnd : dayEnd;

        if (breakOverlapStart < breakOverlapEnd) {
          hours -= (breakOverlapEnd - breakOverlapStart) / (1000 * 60 * 60);
        }
      }

      totalShiftHours += hours;
    }
  });

  return Math.round(totalShiftHours * 100) / 100;
}
